import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { keys } from '@/lib/queryKeys';
import { listVisitDiagnoses, createDiagnosis, updateDiagnosis, retractDiagnosis, listDiagnosesForPatient } from './clinicalApi';
import type { EncounterDiagnosisInput } from './types';

/**
 * Branch-scoped (reached through a branch-scoped visit) — branchVersion is
 * appended so a branch switch mid-render forces a refetch, same convention
 * as useAppointmentSlots.ts/useTodayQueue.ts.
 */
export function useVisitDiagnoses(visitCode: string) {
  const { branchVersion } = useAuth();
  return useQuery({
    queryKey: [...keys.visits.diagnoses(visitCode), branchVersion],
    queryFn: () => listVisitDiagnoses(visitCode),
    enabled: !!visitCode,
  });
}

/** Every diagnosis this patient has ever had, across every visit — tenant-wide,
 * so no branchVersion (same reasoning as usePatientPrescriptions.ts). Used
 * for "repeat from last visit" carry-forward chips, not for a per-visit list. */
export function usePatientDiagnoses(uhid: string | null | undefined) {
  return useQuery({
    queryKey: keys.clinical.encounterDiagnosesForPatient(uhid ?? ''),
    queryFn: () => listDiagnosesForPatient(uhid as string),
    enabled: !!uhid,
  });
}

function invalidateDiagnoses(queryClient: ReturnType<typeof useQueryClient>, visitCode: string, uhid?: string) {
  queryClient.invalidateQueries({ queryKey: keys.visits.diagnoses(visitCode) });
  if (uhid) queryClient.invalidateQueries({ queryKey: keys.clinical.encounterDiagnosesForPatient(uhid) });
}

export function useCreateDiagnosis(visitCode: string, uhid?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: EncounterDiagnosisInput) => createDiagnosis(visitCode, input),
    onSuccess: () => invalidateDiagnoses(queryClient, visitCode, uhid),
  });
}

export function useUpdateDiagnosis(visitCode: string, uhid?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch, ifMatch }: { id: number; patch: Partial<EncounterDiagnosisInput>; ifMatch?: string }) =>
      updateDiagnosis(visitCode, id, patch, ifMatch),
    onSuccess: () => invalidateDiagnoses(queryClient, visitCode, uhid),
  });
}

export function useRetractDiagnosis(visitCode: string, uhid?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => retractDiagnosis(visitCode, id),
    onSuccess: () => invalidateDiagnoses(queryClient, visitCode, uhid),
  });
}
